import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle } from "@/components/ui/SectionTitle";
import {
  CalendarDays,
  CalendarX2,
  Download,
  Eye,
  Paperclip,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import "react-quill-new/dist/quill.snow.css";
import toast from "react-hot-toast";
import { PolicyStatusBadge } from "../../../_components/policy-status-badge";
import { Button } from "@/components/ui/button";
import { useFileDownloader } from "@/hooks/useFileDownloader";
import {
  useGetCompanyPolicyByIdQuery,
  useWithdrawCompanyPolicyMutation,
} from "@/services/(company)/api.company-policies";
import { PATHS } from "@/routes/paths";

const getIconColor = (color: string) => {
  const colors: Record<string, string> = {
    "green-400": "34,197,94",
    "red-400": "239,68,68",
    "blue-400": "59,130,246",
    "orange-400": "251,146,60",
    "gray-400": "156,163,175",
  };
  return colors[color] || "156,163,175";
};

export const PolicyOverview = () => {
  const { t } = useTranslation(["common", "company"]);
  const navigate = useNavigate();
  const { companyPoliciesId } = useParams();
  const { downloadFiles } = useFileDownloader();

  const {
    data: companyPolicyData,
    isLoading,
    isError,
  } = useGetCompanyPolicyByIdQuery(
    { companyPoliciesId: String(companyPoliciesId) },
    { skip: !companyPoliciesId, refetchOnMountOrArgChange: true },
  );
  const [withdrawPolicy, { isLoading: isWithdrawing }] =
    useWithdrawCompanyPolicyMutation();

  const companyPolicy = companyPolicyData?.data;

  const overviewFields = companyPolicy
    ? [
        {
          label: t("company:policies_settings.col_policy_title"),
          value: companyPolicy.title || "-",
        },
        {
          label: t("company:expenses.category"),
          value: companyPolicy.category.name || "-",
        },
        {
          label: t("company:expenses.description"),
          value: companyPolicy.summary || "-",
          className: "col-span-2",
        },
      ]
    : [];

  const handleDownload = (policyUrl: string) => {
    const urls = [policyUrl].filter(Boolean) as string[];
    if (urls.length > 0) {
      downloadFiles(urls);
    }
  };

  const handleWithdraw = async () => {
    if (!companyPoliciesId) return;

    const id = toast.loading(t("common:states.processing"));
    await withdrawPolicy({
      companyPoliciesId: String(companyPoliciesId),
    })
      .unwrap()
      .then((res) => {
        toast.success(res.message, { id });
      })
      .catch((e) =>
        toast.error(
          e?.data?.message ??
            e.data?.errors?.message ??
            t("company:policies_settings.something_went_wrong"),
          {
            id,
          },
        ),
      );
  };

  if (isLoading)
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-2 lg:p-6 bg-white shadow-xs border h-full">
        {/* Left skeleton */}
        <Card className="py-4 lg:p-4 col-span-2 flex flex-col gap-4">
          <CardContent className="p-2 lg:p-0">
            <Skeleton className="w-36 sm:w-48 h-6 mb-4" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Skeleton className="h-16 w-full rounded-sm" />
              <Skeleton className="h-16 w-full rounded-sm" />
            </div>
            <Skeleton className="h-10 w-full mt-4 rounded-md" />
            <Skeleton className="min-h-[30vh] w-full mt-4 rounded-md" />
          </CardContent>
        </Card>

        {/* Right skeleton */}
        <Card className="p-0 h-fit">
          <CardContent className="p-2 lg:p-0">
            <Skeleton className="w-28 sm:w-36 h-6 mb-4" />
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="flex justify-between items-center gap-2 sm:gap-4 mt-2 py-2 px-4 sm:px-8"
              >
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-5 w-20 sm:w-24 rounded-sm" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );

  if (!companyPolicy || isError) {
    toast.error(t("my-space:toasts.company_policy_not_found"));
    navigate(-1);
    return;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-4 p-2  overflow-hidden bg-white lg:p-6 rounded-sm h-full">
      {/* LEFT CARD: Overview */}
      <Card className="py-4 lg:py-4 col-span-2 flex flex-col gap-4 mb-4 lg:mb-0 overflow-hidden">
        <CardContent className="p-0 flex flex-col h-full">
          {/* Scrollable area */}
          <div className="px-6">
            <SectionTitle
              title={t("my-space:company_policy.policy_overview")}
              size="md"
            />
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-6 ">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              {overviewFields.map(({ label, value, className }) => (
                <div
                  key={label}
                  className={`flex flex-col gap-1 bg-gray-100 p-2 rounded-sm ${className}`}
                >
                  <p className="text-[10px] sm:text-xs text-black/50 uppercase">
                    {label}
                  </p>
                  <p className="text-xs sm:text-sm font-medium">{value}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 min-h-[30vh] overflow-y-auto bg-gray-100 p-2 rounded-sm scrollbar-thin">
              <p className="text-[10px] sm:text-xs text-black/50 uppercase mb-1">
                {t("company:policies_settings.policy_content")}
              </p>
              <div className="prose max-w-full">
                <div
                  className="ql-editor"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(companyPolicy.content || "-"),
                  }}
                />
              </div>
            </div>

            {/* Attachments */}
            <div className="mt-4">
              <p className="text-[10px] sm:text-xs text-black/50 uppercase mb-2">
                {t("common:labels.attachments")}
              </p>
              <div
                className={`flex items-center justify-between rounded-sm p-2 overflow-hidden w-1/2 transition
                    ${
                      companyPolicy?.attachmentUrl
                        ? "bg-gray-50 hover:bg-gray-100 cursor-pointer"
                        : "bg-gray-100 cursor-not-allowed opacity-60"
                    }`}
                onClick={() =>
                  companyPolicy?.attachmentUrl &&
                  handleDownload(companyPolicy.attachmentUrl)
                }
              >
                <div className="flex items-center gap-3">
                  <div className="bg-danger-muted p-2 rounded-xs">
                    <Paperclip className="text-danger size-4" />
                  </div>

                  <div>
                    <p className="text-sm font-medium">
                      {/* eslint-disable-next-line i18next/no-literal-string -- file extension */}
                      {companyPolicy.title}.pdf
                    </p>
                    <p className="text-xs text-gray-400">
                      {companyPolicy?.attachmentUrl
                        ? t("my-space:ui.click_to_download")
                        : t("my-space:ui.no_attachment_available")}
                    </p>
                  </div>
                </div>

                <Download
                  className={`size-4 ${companyPolicy?.attachmentUrl ? "text-primary" : "text-gray-400"}`}
                />
              </div>
            </div>
          </div>

          {/* Sticky bottom buttons */}
          <div className="mt-auto border-t p-6 flex justify-end gap-2">
            <Button
              variant="outline"
              className="text-gray-400 hover:text-gray-500"
              onClick={() => navigate(-1)}
            >
              {t("common:buttons.cancel")}
            </Button>
            <Button
              variant="danger"
              onClick={handleWithdraw}
              disabled={isWithdrawing}
            >
              {t("common:buttons.withdraw")}
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                navigate(
                  PATHS.COMPANY.COMPANY_POLICIES_PATHS.EDIT(companyPolicy.id),
                )
              }
            >
              {t("common:buttons.update")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* RIGHT CARD: Details */}
      <Card className="p-0 h-fit">
        <CardContent className="p-1 lg:p-0">
          <div className="flex items-center justify-between border-b p-4">
            <h6 className="font-semibold flex items-center gap-2 text-sm sm:text-lg">
              {t("my-space:company_policy.policy_details")}
            </h6>
          </div>

          <DetailRow
            icon={Eye}
            label={t("common:labels.status")}
            customContent={<PolicyStatusBadge status={companyPolicy.status} />}
            iconColor={"orange-400"}
          />
          <DetailRow
            icon={CalendarDays}
            label={t("company:policies_settings.effective_date")}
            value={
              companyPolicy.effectiveDate
                ? format(new Date(companyPolicy.effectiveDate), "dd/MM/yyyy")
                : "-"
            }
            iconColor="green-400"
          />
          <DetailRow
            icon={CalendarX2}
            label={t("company:policies_settings.expiry_date")}
            value={
              companyPolicy.expiryDate
                ? format(new Date(companyPolicy.expiryDate), "dd/MM/yyyy")
                : "-"
            }
            iconColor="red-400"
          />

          <DetailRow
            icon={Eye}
            label={t("company:policies_settings.visible_to_all_employees")}
            value={
              companyPolicy.isVisibleToAll
                ? t("company:calendars.yes")
                : t("company:calendars.no")
            }
            iconColor="blue-400"
          />
        </CardContent>
      </Card>
    </div>
  );
};

// Reusable DetailRow
interface DetailRowProps {
  icon: any;
  label: string;
  value?: string;
  valueClass?: string;
  iconColor?: string;
  customContent?: React.ReactNode;
}

const DetailRow = ({
  icon: Icon,
  label,
  value,
  valueClass = "",
  iconColor = "gray-400",
  customContent,
}: DetailRowProps) => (
  <div className="flex justify-between items-center gap-2 sm:gap-4 mt-2 p-2 sm:p-3 lg:py-3 lg:px-8">
    <div className="flex gap-2 items-center">
      <div
        className="flex h-8 w-8 sm:h-8 sm:w-8 items-center justify-center rounded-sm"
        style={{ backgroundColor: `rgba(${getIconColor(iconColor)},0.1)` }}
      >
        <Icon size={16} className={`text-${iconColor} sm:text-${iconColor}`} />
      </div>
      <p className="text-[10px] sm:text-xs text-black/50 uppercase font-semibold">
        {label}
      </p>
    </div>
    <div className={`px-2 py-1 sm:px-3 sm:py-1 rounded-sm ${valueClass}`}>
      {customContent || (
        <p className="font-medium text-[10px] sm:text-sm">{value}</p>
      )}
    </div>
  </div>
);
